import { useState } from 'react';
import ModalShell from './ModalShell.jsx';
import { aiClient, AiBudgetExhaustedError } from '../lib/aiClient.js';

const CATEGORIES = ['Unmanned', 'Manned', 'Airspace', 'AI', 'Manufacturing'];

// Add Source modal (Phase 6). Article-only — no podcast tab.
//
// Flow: paste URL → "Scrape" calls /api/ai/scrape (Haiku) → review prefilled
// fields → "Save" inserts via POST /editions/:date/articles with
// editorPromoted=true. The returned Edition flows back to useEditionDraft so
// the new article shows up in the brief immediately.
export default function AddSourceModal({ isoDate, onClose, onInsert }) {
  const [step, setStep] = useState('url'); // 'url' | 'preview'
  const [url, setUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);
  const [draft, setDraft] = useState(null); // article + keyClaims
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  async function onScrape(e) {
    e?.preventDefault?.();
    setScrapeError(null);
    if (!/^https?:\/\//i.test(url.trim())) {
      setScrapeError('Paste a full URL starting with http(s)://');
      return;
    }
    setScraping(true);
    try {
      const res = await aiClient.scrape({ url: url.trim(), isoDate });
      setDraft({
        ...res.article,
        keyClaims: res.keyClaims || [],
        extracted: res.extracted || null,
      });
      setStep('preview');
    } catch (err) {
      if (err instanceof AiBudgetExhaustedError) {
        onClose?.(); // Editor.jsx surfaces the global AI-budget modal.
        return;
      }
      setScrapeError(err.message || 'Scrape failed.');
    } finally {
      setScraping(false);
    }
  }

  function updateDraft(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
  }

  async function onSave() {
    if (!draft) return;
    setSaveError(null);
    setSaving(true);
    try {
      const payload = {
        headline: draft.headline,
        summary: draft.summary,
        keyTakeaway: draft.keyTakeaway,
        category: draft.category || undefined,
        primaryCompany: draft.primaryCompany || undefined,
        imageUrl: draft.imageUrl || undefined,
        source: draft.source,
      };
      await onInsert(payload);
      onClose?.();
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title="Add Source"
      eyebrow={step === 'url' ? 'Step 1 · Paste URL' : 'Step 2 · Review'}
      onClose={onClose}
      widthClass="max-w-3xl"
    >
      {step === 'url' && (
        <form onSubmit={onScrape} className="space-y-3">
          <div>
            <label className="hxd-label">Article URL</label>
            <input
              type="url"
              autoFocus
              className="hxd-input"
              placeholder="https://aviationweek.com/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="text-[11px] text-hxd-muted mt-1">
              We'll fetch the page and use Claude Haiku to draft headline, summary, takeaway,
              and category. You'll review before saving.
            </p>
          </div>

          {scrapeError && <div className="text-[12px] text-hxd-err">{scrapeError}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="hxd-btn hxd-btn-ghost hxd-btn-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={scraping || !url.trim()}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              {scraping ? 'Scraping…' : 'Scrape'}
            </button>
          </div>
        </form>
      )}

      {step === 'preview' && draft && (
        <div className="space-y-4">
          <div className="text-[11px] text-hxd-muted flex flex-wrap gap-2 items-center">
            <span>From:</span>
            <a
              href={draft.source?.url}
              target="_blank"
              rel="noreferrer"
              className="text-hxd-orange hover:underline break-all"
            >
              {draft.source?.displayName || draft.source?.domain || draft.source?.url}
            </a>
          </div>

          <div>
            <label className="hxd-label">Headline</label>
            <input
              className="hxd-input font-semibold"
              value={draft.headline || ''}
              onChange={(e) => updateDraft('headline', e.target.value)}
            />
          </div>

          <div>
            <label className="hxd-label">Summary</label>
            <textarea
              className="hxd-input min-h-[120px]"
              rows={5}
              value={draft.summary || ''}
              onChange={(e) => updateDraft('summary', e.target.value)}
            />
          </div>

          <div>
            <label className="hxd-label">Key Takeaway</label>
            <input
              className="hxd-input"
              value={draft.keyTakeaway || ''}
              onChange={(e) => updateDraft('keyTakeaway', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="hxd-label">Category</label>
              <select
                className="hxd-input"
                value={draft.category || ''}
                onChange={(e) => updateDraft('category', e.target.value)}
              >
                <option value="">— Choose —</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="hxd-label">Primary Company</label>
              <input
                className="hxd-input"
                value={draft.primaryCompany || ''}
                onChange={(e) => updateDraft('primaryCompany', e.target.value)}
                placeholder="(optional)"
              />
            </div>
          </div>

          {draft.imageUrl && (
            <div>
              <label className="hxd-label">Image (from OpenGraph)</label>
              <div className="flex items-start gap-3">
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="w-32 h-20 object-cover border border-hxd-border-soft rounded-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <input
                  className="hxd-input flex-1"
                  value={draft.imageUrl || ''}
                  onChange={(e) => updateDraft('imageUrl', e.target.value)}
                />
              </div>
            </div>
          )}

          {Array.isArray(draft.keyClaims) && draft.keyClaims.length > 0 && (
            <div>
              <div className="hxd-label">Key claims (verify-ready)</div>
              <ul className="list-disc pl-5 text-[12px] text-hxd-muted space-y-1">
                {draft.keyClaims.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {saveError && <div className="text-[12px] text-hxd-err">{saveError}</div>}

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-hxd-border-soft">
            <button
              type="button"
              onClick={() => setStep('url')}
              className="hxd-btn hxd-btn-ghost hxd-btn-sm"
            >
              ← Re-scrape
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="hxd-btn hxd-btn-ghost hxd-btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving || !draft.headline?.trim()}
                className="hxd-btn hxd-btn-primary hxd-btn-sm"
              >
                {saving ? 'Saving…' : 'Add to brief'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
